import { TempNode } from 'three/webgpu';
import { nodeObject, uv, vec4, vec3, vec2, float, sin, cos, fract, dot, floor, mix, pow, length, uniform, convertToTexture } from 'three/tsl';

/**
 * CRT-style glitch effect with scan lines, static noise, horizontal displacement, and vignette
 */
class CRTNode extends TempNode {
	static get type() {
		return 'CRTNode';
	}

	constructor(textureNode, scanlineIntensity = 0.3, scanlineCount = 800.0, noiseIntensity = 0.1, distortionIntensity = 0.02, vignetteIntensity = 0.5, time = 0) {
		super('vec4');
		this.textureNode = convertToTexture(textureNode);
		this.scanlineIntensity = uniform(scanlineIntensity);
		this.scanlineCount = uniform(scanlineCount);
		this.noiseIntensity = uniform(noiseIntensity);
		this.distortionIntensity = uniform(distortionIntensity);
		this.vignetteIntensity = uniform(vignetteIntensity);
		this.time = uniform(time);
	}

	setup() {
		const { textureNode, scanlineIntensity, scanlineCount, noiseIntensity, distortionIntensity, vignetteIntensity, time } = this;
		const uvNode = textureNode.uvNode || uv();
		
		// Step 4: Add horizontal and vertical displacement glitches
		// Horizontal displacement - random bands shift left/right
		const blockY = floor(uvNode.y.mul(20.0));
		const blockNoiseH = fract(sin(blockY.add(time.mul(2.0))).mul(43758.5453));
		
		const displacementH = fract(sin(blockY.mul(12.9898)).mul(43758.5453)).sub(0.5).mul(distortionIntensity);
		const glitchMaskH = blockNoiseH.greaterThan(0.85);
		const displacementAmountH = mix(float(0.0), displacementH, glitchMaskH);
		
		// Vertical displacement - random bands shift up/down
		const blockX = floor(uvNode.x.mul(30.0));
		const blockNoiseV = fract(sin(blockX.add(time.mul(1.5))).mul(43758.5453));
		
		const displacementV = fract(sin(blockX.mul(78.233)).mul(43758.5453)).sub(0.5).mul(distortionIntensity.mul(0.5));
		const glitchMaskV = blockNoiseV.greaterThan(0.9);
		const displacementAmountV = mix(float(0.0), displacementV, glitchMaskV);
		
		// Apply both horizontal and vertical displacement
		const distortedUV = vec2(
			uvNode.x.add(displacementAmountH),
			uvNode.y.add(displacementAmountV)
		);
		
		// Sample the input texture with distorted UVs
		const color = textureNode.sample(distortedUV);
		
		// Step 3: Add animated noise/static (pre-multiplied)
		const noiseCoord = vec2(
			uvNode.x.add(time),
			uvNode.y.add(time.mul(0.7))
		);
		
		const noise = fract(
			sin(dot(noiseCoord, vec2(12.9898, 78.233)))
			.mul(43758.5453)
		);
		
		// Pre-multiply noise (darken where noise is low, brighten where noise is high)
		const noiseModulation = mix(
			float(1.0).sub(noiseIntensity),
			float(1.0).add(noiseIntensity),
			noise
		);
		const colorWithNoise = color.mul(noiseModulation);
		
		// Step 2: Add scan lines
		const scanline = sin(uvNode.y.mul(scanlineCount)).mul(scanlineIntensity);
		const scanlineEffect = float(1.0).sub(scanline);
		
		// Step 5: Add vignette (darker edges)
		// Calculate distance from center
		const center = vec2(0.5, 0.5);
		const distFromCenter = length(uvNode.sub(center));
		
		// Create smooth vignette falloff
		const vignette = float(1.0).sub(
			pow(distFromCenter.mul(1.5), float(2.0)).mul(vignetteIntensity)
		);
		
		// Combine all effects
		const withEffects = colorWithNoise.mul(scanlineEffect);
		const finalColor = withEffects.mul(vignette);
		
		return vec4(finalColor.rgb, color.a);
	}
}

export default CRTNode;

export const crt = (node, scanlineIntensity, scanlineCount, noiseIntensity, distortionIntensity, vignetteIntensity, time) =>
	nodeObject(new CRTNode(node, scanlineIntensity, scanlineCount, noiseIntensity, distortionIntensity, vignetteIntensity, time));
